import { apiInstance } from "./index";

/**
 * Shape data trả về từ compat layer GoldenPen (giữ nguyên hợp đồng app cũ baohanh-barcode).
 */
/** Trạng thái tem theo `stamp_codes.status`, dùng để FE chọn giao diện. */
export type WarrantyState = "activated" | "sold" | "unsold" | "locked" | "blocked";

/** Message theo trạng thái, cấu hình trong scan-setting (admin đổi được). */
export interface WarrantyMessage {
  logo?: string;
  title?: string;
  content?: string;
}

/** Cờ cấu hình hiển thị lấy từ scan-setting. */
export interface WarrantyConfig {
  is_show_product_price: boolean;
  is_show_customer_info: boolean;
  is_show_verification_form: boolean;
  /** HTML admin soạn — hiện ở CUỐI bảng thông tin, chỉ khi tem đã kích hoạt. */
  landing_page_note_content: string;
  /** HTML admin soạn — hiện NGAY DƯỚI nút kích hoạt (cả 2 chế độ của /kichhoat). */
  landing_activate_note_content?: string;
  template_id: string | null;
}

/** Cấu hình template hiển thị (đã áp customization của company). */
export interface WarrantyTemplate {
  code: string; // vd: mrw_template_4
  name: string;
  data?: Record<string, string>; // vd: color_success, color_error, text_default...
}

export interface WarrantyProductBlock {
  name: string;
  category?: string;
  brand?: string;
  unit?: string;
  price?: number;
  image?: string;
  images?: string[];
  description?: string;
  item_code?: string;
  custom_fields?: Array<{ name?: string; label?: string; value?: unknown }>;
  is_show_price?: boolean;
}

export interface WarrantyCompanyBlock {
  name?: string;
  address?: string;
  phone?: string;
  email?: string;
  website?: string;
  logo?: string;
  images?: string[];
  description?: string;
  social_networks?: Array<{ key: string; value: string }>;
}

export interface WarrantyBlock {
  has_warranty: boolean;
  warranty_days: number;
  warranty_start: string;
  warranty_end: string;
  warranty_days_left: number | null;
  activated_at: string;
  package_name?: string;
  is_pisen_care_plus: boolean;
  service_name?: string;
  // S1 — tem cũ khách trả lại (status warranty_replaced): "Hàng bảo hành trả lại".
  is_exchanged: boolean;
  // S2 — tem mới thay thế, kế thừa bảo hành (có warranty_replaced_at, status vẫn
  // activated/sold): "Hàng bảo hành đổi mới".
  is_new_exchange?: boolean;
}

export interface WarrantyVerificationBlock {
  scan_count: number;
  unique_scan_count: number;
  first_verification_at: string;
}

export interface WarrantyCustomerBlock {
  name?: string;
  phone?: string;
  email?: string;
  address?: string;
}

/** Nhà mạng cho khách chọn (C14). */
export interface ScratchCardNetworkOption {
  network: number; // 1..5
  name: string; // Viettel / Mobiphone...
}

/** Loại phần thưởng: phát mã thẻ cào hoặc nạp trực tiếp SĐT. */
export type ScratchCardRewardType = "card" | "topup";

/**
 * Thẻ cào / nạp tự động phát khi kích hoạt bảo hành (C11–C14 + Topup).
 *  - reward_type "card" : phát mã thẻ cào (issued -> card_serial + card_code).
 *  - reward_type "topup": nạp trực tiếp vào SĐT khách (bất đồng bộ).
 *    + needs_phone: hiển thị ô nhập SĐT (khách chưa có SĐT hợp lệ — D6).
 *    + status "processing": đang nạp (chờ kết quả); "issued": đã nạp xong.
 *  - card "processing": đối tác còn giữ giao dịch, chờ đối soát (không cho thử lại).
 *  - card "manual_review": đối tác đã phát nhưng hệ thống mất mã thẻ — khách liên hệ hotline.
 *  - needs_network_selection: hiển thị bộ chọn nhà mạng (chỉ luồng card).
 *  - error: báo lỗi (cho retry với nhà mạng đã chọn / nhập lại SĐT).
 */
export interface ScratchCardRewardBlock {
  reward_type?: ScratchCardRewardType;
  status: "pending" | "processing" | "issued" | "failed" | "manual_review";
  card_amount: number;
  network: number | null;
  network_name: string | null;
  card_serial: string | null;
  card_code: string | null;
  card_expired_at: string | null;
  issued_at: string | null;
  needs_network_selection: boolean;
  available_networks: ScratchCardNetworkOption[];
  // Số lượt khách còn được bấm "Thử lại" phát thẻ (tối đa 5, kể cả khi đã failed).
  retries_left?: number;
  // Ghi chú khách hàng (áp dụng cả phát thẻ cào lẫn nạp tự động). Lần kích hoạt đầu
  // hiện cùng khối trả thưởng; các lần sau hiện dạng help text dưới thông tin phần thưởng.
  message?: string | null;
  // --- Topup ---
  phone?: string | null; // SĐT đã che giữa (masked)
  needs_phone?: boolean;
  error: string | null;
}

export interface WarrantyData {
  product_name: string;
  warranty_days: number;
  warranty_start: string; // dd/mm/yyyy
  warranty_end: string; // dd/mm/yyyy
  is_client: boolean;
  name: string;
  phone: string;
  is_pisen_care_plus: boolean;
  isExchangedBarcode: boolean;
  history_changed: unknown[];
  status?: string; // "not_found" khi tem không hợp lệ

  // --- Trạng thái tem chính xác (BE GoldenpenWarrantyController) ---
  state?: WarrantyState;
  is_activated?: boolean;
  can_activate?: boolean;
  serial?: string;
  code?: string;
  product_code?: string;
  product_image?: string;
  warranty_days_left?: number | null;
  activated_at?: string; // dd/mm/yyyy

  // --- Khối giàu + cấu hình hiển thị (tận dụng scan-setting) ---
  message?: WarrantyMessage | null;
  config?: WarrantyConfig;
  product?: WarrantyProductBlock | null;
  company?: WarrantyCompanyBlock | null;
  template?: WarrantyTemplate | null;
  warranty?: WarrantyBlock;
  verification?: WarrantyVerificationBlock;
  customer?: WarrantyCustomerBlock | null;

  // --- Thẻ cào (C11–C14) — null nếu tem không thuộc campaign nào ---
  scratch_card_reward?: ScratchCardRewardBlock | null;
}

/**
 * Endpoint compat GoldenPen. `ApiRoute` (examyou/rest-api) tự thêm prefix `/api/v1/`,
 * nên BE serve tại `/api/v1/check` + `/api/v1/scan` — FE gọi đúng path này
 * (giống các call khác của landing page, ví dụ `/api/v1/verify/scan-qrcode`).
 * Tên endpoint `check`/`scan` giữ nguyên như app cũ; QR/URL đã in được bảo toàn
 * ở route trang `/kichhoat` (không phụ thuộc path API).
 */
const CHECK_URL = "/api/v1/check";
const SCAN_URL = "/api/v1/scan";
const LANDING_CONFIG_URL = "/api/v1/landing-config";
const SELECT_TELECOM_URL = "/api/v1/scan-select-telecom";
const SET_TOPUP_PHONE_URL = "/api/v1/scan-set-topup-phone";

/**
 * Cấu hình hiển thị khi CHƯA có tem (form nhập tay ở `/kichhoat`).
 * Chỉ gồm phần vốn hiển thị công khai: theme + nội dung dưới nút kích hoạt.
 */
export interface LandingConfigData {
  template: WarrantyTemplate | null;
  config: {
    landing_activate_note_content: string;
    template_id: string | null;
  } | null;
}

// GET /check?series=&code= — đọc trạng thái tem + bảo hành (không kích hoạt)
export const apiCheckWarranty = async (params: { series: string; code: string }): Promise<WarrantyData> => {
  const response = await apiInstance.get(CHECK_URL, { params });
  return response.data?.data as WarrantyData;
};

// GET /landing-config — theme + nội dung tuỳ chỉnh cho màn chưa có serial
export const apiLandingConfig = async (): Promise<LandingConfigData> => {
  const response = await apiInstance.get(LANDING_CONFIG_URL);
  return response.data?.data as LandingConfigData;
};

// POST /scan { series, code, name, phone } — kích hoạt bảo hành
export const apiScanWarranty = async (payload: {
  series: string;
  code: string;
  name: string;
  phone: string;
}): Promise<WarrantyData> => {
  const response = await apiInstance.post(SCAN_URL, payload);
  return response.data?.data as WarrantyData;
};

// POST /scan-select-telecom { series, code, network } — C14: chọn nhà mạng nhận thẻ
export const apiSelectTelecom = async (payload: {
  series: string;
  code: string;
  network: number;
}): Promise<WarrantyData> => {
  const response = await apiInstance.post(SELECT_TELECOM_URL, payload);
  return response.data?.data as WarrantyData;
};

// POST /scan-set-topup-phone { series, code, phone } — D6: nhập SĐT để nhận nạp tự động
export const apiSetTopupPhone = async (payload: {
  series: string;
  code: string;
  phone: string;
}): Promise<WarrantyData> => {
  const response = await apiInstance.post(SET_TOPUP_PHONE_URL, payload);
  return response.data?.data as WarrantyData;
};
